// src/quality-management/utils/taskSchedule.ts
// Classifica uma tarefa quanto ao cronograma (status x prazo) e fornece cores/labels.
// Usado pelo Plano de Entregas (timeline) e pelos widgets de risco do Analytics.

import { Task } from '../services/api/types';

export type ScheduleState =
  | 'completed'
  | 'overdue'
  | 'at_risk'
  | 'in_progress'
  | 'future'
  | 'no_date';

const DAY = 86_400_000;

// Limiares da regra de atenção (amarelo)
const AT_RISK_DAYS = 3; // últimos 3 dias
const AT_RISK_FRACTION = 0.3; // ou resta <= 30% do tempo total

function parseDate(v?: string | null): Date | null {
  if (!v) return null;
  const iso = v.includes('T') ? v : `${v}T00:00:00`;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Data mínima de início (YYYY-MM-DD) de uma task vinculada a uma predecessora:
// um dia após a conclusão planejada dela (due_date; na falta, task_date).
export function predecessorMinStart(pred: {
  due_date?: string | null;
  task_date?: string | null;
}): string | null {
  const raw = pred.due_date || pred.task_date;
  if (!raw) return null;
  const day = raw.slice(0, 10); // YYYY-MM-DD (aceita ISO completo ou só a data)
  const d = new Date(`${day}T00:00:00Z`);
  if (isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

// Formata um dia ISO (YYYY-MM-DD) como DD/MM/YYYY
export function formatDayBR(isoDay: string): string {
  const [y, m, dd] = isoDay.slice(0, 10).split('-');
  return `${dd}/${m}/${y}`;
}

// ---------------------------------------------------------------------------
// Distribuição das horas previstas ao longo dos dias da tarefa.
//
// As horas previstas descrevem a tarefa inteira, não um único dia. Somar o
// valor cheio no dia de início fazia um dia isolado somar mais horas que a
// semana toda. Aqui as horas são divididas pelos dias úteis do intervalo.
// ---------------------------------------------------------------------------

/** Dia ISO (YYYY-MM-DD) de um Date, em UTC. */
export const isoDay = (d: Date): string => d.toISOString().split('T')[0];

/** Intervalo da tarefa como dias ISO, já ordenado. */
function faixaDeDias(task: Task): { inicio: string; fim: string } | null {
  const s = task.task_date ? task.task_date.slice(0, 10) : null;
  const e = task.due_date ? task.due_date.slice(0, 10) : null;
  const inicio = s || e;
  const fim = e || s;
  if (!inicio || !fim) return null;
  return inicio <= fim ? { inicio, fim } : { inicio: fim, fim: inicio };
}

/** A tarefa ocupa o dia informado (YYYY-MM-DD)? */
export function taskCoversDay(task: Task, dia: string): boolean {
  const f = faixaDeDias(task);
  return !!f && dia >= f.inicio && dia <= f.fim;
}

const ehFimDeSemana = (dia: string): boolean => {
  const dow = new Date(`${dia}T00:00:00Z`).getUTCDay();
  return dow === 0 || dow === 6;
};

/** Dias úteis (segunda a sexta) ocupados pela tarefa. Zero se só fim de semana. */
function diasUteisDaTarefa(task: Task): number {
  const f = faixaDeDias(task);
  if (!f) return 0;
  let n = 0;
  const d = new Date(`${f.inicio}T00:00:00Z`);
  const fim = new Date(`${f.fim}T00:00:00Z`);
  while (d <= fim) {
    if (!ehFimDeSemana(isoDay(d))) n += 1;
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return n;
}

/** Total previsto da tarefa, somando hora extra já aprovada. */
function totalPrevisto(task: Task): number {
  const extra =
    task.has_overtime && task.overtime_approval_status === 'approved' ? task.overtime_hours || 0 : 0;
  return (task.expected_hours || 0) + extra;
}

/**
 * Horas previstas que a tarefa representa num dia específico.
 * Distribui o total pelos dias úteis do intervalo; tarefas que caem apenas em
 * fim de semana mantêm as horas no próprio dia.
 */
export function plannedHoursForDay(task: Task, dia: string): number {
  if (!taskCoversDay(task, dia)) return 0;

  const uteis = diasUteisDaTarefa(task);
  const total = totalPrevisto(task);
  if (total <= 0) return 0;

  if (uteis === 0) {
    // Tarefa inteiramente em fim de semana: divide pelos dias do intervalo
    const f = faixaDeDias(task)!;
    const dias =
      Math.round(
        (new Date(`${f.fim}T00:00:00Z`).getTime() - new Date(`${f.inicio}T00:00:00Z`).getTime()) / 86_400_000
      ) + 1;
    return total / Math.max(1, dias);
  }

  return ehFimDeSemana(dia) ? 0 : total / uteis;
}

// Intervalo da tarefa (início = task_date; fim = due_date; com fallbacks)
export function getTaskRange(task: Task): { start: Date | null; end: Date | null } {
  const startRaw = parseDate(task.task_date) || parseDate(task.due_date);
  const endRaw = parseDate(task.due_date) || parseDate(task.task_date);
  return {
    start: startRaw ? startOfDay(startRaw) : null,
    end: endRaw ? startOfDay(endRaw) : null,
  };
}

export function taskScheduleState(task: Task, now: Date = new Date()): ScheduleState {
  if (task.status === 'completed') return 'completed';

  const { start, end } = getTaskRange(task);
  if (!start || !end) return 'no_date';

  const today = startOfDay(now).getTime();
  const startMs = start.getTime();
  const endMs = end.getTime();

  if (endMs < today) return 'overdue';
  if (today < startMs) return 'future';

  // Já iniciada e dentro do prazo: avaliar atenção
  const daysLeft = Math.round((endMs - today) / DAY);
  const totalDays = Math.max(1, Math.round((endMs - startMs) / DAY) + 1);
  const elapsed = Math.round((today - startMs) / DAY) + 1;
  const remainingFraction = (totalDays - elapsed) / totalDays;

  if (daysLeft <= AT_RISK_DAYS || remainingFraction <= AT_RISK_FRACTION) return 'at_risk';
  return 'in_progress';
}

export interface ScheduleStyle {
  label: string;
  bar: string; // classe de fundo da barra
  text: string; // classe de cor de texto/legenda
  ring: string; // classe de anel/borda
}

export const SCHEDULE_STYLES: Record<ScheduleState, ScheduleStyle> = {
  completed: { label: 'Concluída', bar: 'bg-success', text: 'text-success', ring: 'ring-success/30' },
  overdue: { label: 'Atrasada', bar: 'bg-danger', text: 'text-danger', ring: 'ring-danger/30' },
  at_risk: { label: 'Em risco', bar: 'bg-warning', text: 'text-warning', ring: 'ring-warning/30' },
  in_progress: { label: 'Em andamento', bar: 'bg-brand', text: 'text-brand', ring: 'ring-brand/30' },
  future: { label: 'Programada', bar: 'bg-foreground-subtle/40', text: 'text-foreground-muted', ring: 'ring-border' },
  no_date: { label: 'Sem data', bar: 'bg-surface-3', text: 'text-foreground-subtle', ring: 'ring-border' },
};

// Ordem usada em legendas e contagens (exclui no_date)
export const SCHEDULE_ORDER: ScheduleState[] = ['overdue', 'at_risk', 'in_progress', 'future', 'completed'];

// Conta tarefas por estado de cronograma
export function summarizeSchedule(tasks: Task[], now: Date = new Date()): Record<ScheduleState, number> {
  const acc: Record<ScheduleState, number> = {
    completed: 0,
    overdue: 0,
    at_risk: 0,
    in_progress: 0,
    future: 0,
    no_date: 0,
  };
  tasks.forEach((t) => {
    acc[taskScheduleState(t, now)] += 1;
  });
  return acc;
}
